import React, { createContext, useContext, useState, useCallback } from 'react';
import { 
  CheckCircleIcon, 
  ExclamationCircleIcon, 
  InformationCircleIcon, 
  XMarkIcon,
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';

const AlertContext = createContext(null);

export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);

  const showAlert = useCallback((text, type = 'info', duration = 5000) => {
    const id = Date.now();
    setAlerts(prev => [...prev, { id, text, type }]);

    if (duration) {
      setTimeout(() => {
        removeAlert(id);
      }, duration);
    }
  }, []);

  const removeAlert = useCallback((id) => {
    setAlerts(prev => prev.filter(alert => alert.id !== id));
  }, []);

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
        {alerts.map(alert => (
          <AlertItem 
            key={alert.id} 
            alert={alert} 
            onClose={() => removeAlert(alert.id)} 
          />
        ))}
      </div>
    </AlertContext.Provider>
  );
};

const AlertItem = ({ alert, onClose }) => {
  const { type, text } = alert;

  const styles = {
    success: 'bg-green-600 border-green-500 shadow-green-900/20',
    error: 'bg-red-600 border-red-500 shadow-red-900/20',
    warning: 'bg-yellow-600 border-yellow-500 shadow-yellow-900/20',
    info: 'bg-blue-600 border-blue-500 shadow-blue-900/20'
  };

  const Icons = {
    success: CheckCircleIcon,
    error: ExclamationCircleIcon,
    warning: ExclamationTriangleIcon,
    info: InformationCircleIcon
  };

  const Icon = Icons[type] || Icons.info;

  return (
    <div 
      className={`pointer-events-auto flex items-start gap-3 min-w-[300px] max-w-md p-4 rounded-xl border shadow-xl text-white animate-in slide-in-from-right-full duration-300 ${styles[type] || styles.info}`}
    >
      <Icon className="w-6 h-6 flex-shrink-0" />
      <div className="flex-1 text-sm font-medium pt-0.5">
        {text}
      </div>
      <button 
        onClick={onClose}
        className="flex-shrink-0 hover:bg-black/10 rounded-lg p-0.5 transition"
      >
        <XMarkIcon className="w-5 h-5" />
      </button>
    </div>
  );
};

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};
